// @vendor
const React = require('react');
const { isEmpty, find, reduce, filter, isNull, isUndefined } = require('lodash');
const { DOCUMENTS_STATUS } = require('constants/mortgageRequestHolderData');
const { FormattedText, formatText } = require('core/i18n').i18n;

// @utilities
const { getSplittedNumber } = require('utilities/currencyHelper');

// @contants
const DOCUMENTS_TYPES = require('constants/mortgageRequestDocumentsTypes');
const { okInsuranceConstants: { insuranceRequestHome } } = require('ok-insurance');
const { EURO_TEXT } = require('constants/index');
const { GENERAL_DATA, MORTGAGE_TYPE_BY_PRODUCT_TYPE } = require('constants/mortgageRequestMortgageDataTypes');

// @components
const MortgageRequestCurrencyHelp = require('components/mortgageRequest/mortgageRequestCurrencyHelp/mortgageRequestCurrencyHelp');

const SIMPLE_NOTE = 'simple-note';

function removeDisabledEstates(estatesArray, summaryDocsState, { forString } = {}) {
    const estatesObjects = reduce(estatesArray, (previous, current) => {
        return {
            ...previous,
            [current.bienId]: current
        }
    }, {})
    const estatesDocumentsList = summaryDocsState.data;
    const estatesDocuments = reduce(estatesArray, (previous, estate) => {
        const estateDocuments = find(estatesDocumentsList, file => (
            file.bienId === estate.bienId && file.tipoDocumentoId === DOCUMENTS_TYPES.NS_CLI
        ));
        if (estateDocuments) {
            return {
                ...previous,
                [estate.bienId]: estateDocuments
            };
        }

        return previous;
    }, {});
    const filteredEstates = filter(estatesObjects, (estate) => {
        const estateDocument = estatesDocuments[estate.bienId];
        const isDocumentUploaded = (
            estateDocument.estado !== DOCUMENTS_STATUS.NONE &&
            estateDocument.estado !== DOCUMENTS_STATUS.PEND_APORTAR
        );
        const estateIsDisabled = (
            isDocumentUploaded && (estate.notaSimplePropia && forString === SIMPLE_NOTE)
        );
        const KEEP = true;
        const REMOVE = false;

        return estateIsDisabled ? REMOVE : KEEP;
    });

    return filteredEstates;
}

function getOnlyEstatesToRequest(estates, estatesState) {
    const clone = estates.filter(estate => {
        const estateIsToRequest = !!estatesState.find((estateStateValue, estateStateId) => (
            `${estate.get('bienId')}` === `${estateStateId}`
        ));
        return estateIsToRequest;
    });

    return clone.toJS();
}

// summaryDocsState = mortgageRequestSummaryDocumentation // TODO: DELETE
function getEstatesToRequest(immEstates, summaryDocsState, { forString = SIMPLE_NOTE } = {}) {
    const estates = immEstates.get('list');
    const estatesState = immEstates
        .get('estatesState')
        .filter(estate => (
            estate.get('value') === 'request'
        ));
    let estatesToRequest = getOnlyEstatesToRequest(estates, estatesState);

    if (summaryDocsState) {
        estatesToRequest = removeDisabledEstates(
            estatesToRequest,
            summaryDocsState.toJS(),
            { forString }
        );
    }

    return estatesToRequest;
}

function shouldGoToEstatesListPage(immEstates, history) {
    function goToEstateListPage(history) {
        history.push('/myprofile/mortgage/simple-note-result');
    }
    const estatesToRequest = getEstatesToRequest(immEstates);
    const shouldRedirect = isEmpty(estatesToRequest);

    if (shouldRedirect) {
        goToEstateListPage(history);
    }
}

function checkIfBudgetIsConfirmed(relatedState) {
    if (relatedState) {
        const uiStep = relatedState.getIn(['flowData', 'uiStep']);
        const { FLOW_STEPS } = insuranceRequestHome;
        const currentWizardStep = FLOW_STEPS[uiStep];
        const budgetConfirmed = currentWizardStep >= 4;

        return budgetConfirmed;
    }

    return false;
}

const validateSecondPeriodIntrest = (cuotaMensual, cuotaMensualResto) => {
    return !isNull(cuotaMensualResto) && !isUndefined(cuotaMensualResto) && Number(cuotaMensual.toFixed(2)) < Number(cuotaMensualResto.toFixed(2));
}

const mortgagesIntrestPeriodSubtitle = (mortgagesSubtitleText, values) => {
    return (<FormattedText
        injectedStrings={values}
        value={mortgagesSubtitleText}
    />);
}

const getMortgageFeeDecimalValue = (value) => {
    return {
        currency: value,
        symbol: EURO_TEXT,
        withDecimals: true
    };
}

const mortgagePreApprovalSecondIntrestPeriodSubtitle = (cuotaMensual, cuotaMensualResto, differential, mortgageTae) => {
    const preapprovalQuotaParams = getMortgageFeeDecimalValue(cuotaMensualResto);
    const secondPeriodIntrest = MortgageRequestCurrencyHelp(preapprovalQuotaParams);

    return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
        ? mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-secondPeroidMortgagesTAESubtitleText', [secondPeriodIntrest, differential, mortgageTae])
        : mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-firstPeroidMortgagesTAESubtitleText', [differential, mortgageTae]);
}

const mortgagesPreApprovalIntrestPeriodSubtitle = (mortgageTin, mortgageTae, conditions) => {
    const interestType = conditions.get('tipoInteres', '') || '';
    const currentDiferencialNumber = conditions.get('diferencial', 0) || 0;
    const cuotaMensualResto = conditions.get('cuotaMensualResto');
    const cuotaMensual = conditions.get('cuotaMensual');
    const differentialTransform = getSplittedNumber(currentDiferencialNumber);
    const differential = `${differentialTransform.whole}${differentialTransform.decimalMark}${differentialTransform.decimals}%`;
    const fixedTerm = conditions.get('fixedTerm');
    switch (interestType) {
        case '01':
            return mortgagesIntrestPeriodSubtitle('mortgageRequestPreApprovalStepFourth-mortgageRequestPreApprovalStepFourth_quota', [mortgageTin, mortgageTae]);
        case '02':
            return (<span>
                {mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-variableMortgagesTINSubtitleText', [mortgageTin])}
                <br />
                {mortgagePreApprovalSecondIntrestPeriodSubtitle(cuotaMensual, cuotaMensualResto, differential, mortgageTae)}
            </span>);
        case '03':
            return (<span>
                {mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-mixedMortgagesTINSubtitleText', [mortgageTin, fixedTerm])}
                <br />
                {mortgagePreApprovalSecondIntrestPeriodSubtitle(cuotaMensual, cuotaMensualResto, differential, mortgageTae)}
            </span>);

        default:
            return '';
    }
}

const mortgagesMonthlyFeeSubtitle = (cuotaMensual, cuotaMensualResto, isVariableMortgages) => {
    const firstPeriodIntrest = MortgageRequestCurrencyHelp(getMortgageFeeDecimalValue(cuotaMensual));
    const secondPeriodIntrest = MortgageRequestCurrencyHelp(getMortgageFeeDecimalValue(cuotaMensualResto));

    return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
        ?
        (<span>
            {!isVariableMortgages
                ? mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-mixedMortgagesMonthlyFeeInfoSubtitleText', [firstPeriodIntrest, '10'])
                : mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-variableMortgagesMonthlyFeeInfoSubtitleText', [firstPeriodIntrest])}
            <br />
            {mortgagesIntrestPeriodSubtitle('mortgageRequestPanel-mortgagesMonthlyFeesnfoSubtitleText', [secondPeriodIntrest])}
        </span>)
        : firstPeriodIntrest;
}

const mortgagesMonthlyFeeSubtitleText = (conditions) => {
    const interestType = conditions.get('tipoInteres', '') || '';
    const cuotaMensualResto = conditions.get('cuotaMensualResto');
    const cuotaMensual = conditions.get('cuotaMensual');

    switch (interestType) {
        case '01':
            return MortgageRequestCurrencyHelp({ currency: cuotaMensual });
        case '02':
            return mortgagesMonthlyFeeSubtitle(cuotaMensual, cuotaMensualResto, true);
        case '03':
            return mortgagesMonthlyFeeSubtitle(cuotaMensual, cuotaMensualResto, false);

        default:
            return '';
    }
}

const mortgagesPanelSubtitleText = (conditions) => {
    const interestType = conditions.get('tipoInteres', '') || '';
    const currentInterestNumber = conditions.get('interes', 0) || 0;
    const interestTransform = getSplittedNumber(currentInterestNumber);
    const interest = `${interestTransform.whole}${interestTransform.decimalMark}${interestTransform.decimals}`;
    const currentDiferencialNumber = conditions.get('diferencial', 0) || 0;
    const differentialTransform = getSplittedNumber(currentDiferencialNumber);
    const differential = `${differentialTransform.whole}${differentialTransform.decimalMark}${differentialTransform.decimals}`;
    const cuotaMensual = conditions.get('cuotaMensual');
    const cuotaMensualResto = conditions.get('cuotaMensualResto');
    const secondPeriodIntrest = MortgageRequestCurrencyHelp(getMortgageFeeDecimalValue(cuotaMensualResto));
    const fixedTerm = conditions.get('fixedTerm');


    switch (interestType) {
        case '01':
            return `${interest}%`;
        case '02':
            return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
                ? `${interest}% ${formatText('mortgageRequestPanel-variableMortgagesPanelSubtitleText', [secondPeriodIntrest])} + ${differential}% ${formatText('mortgageRequestPanel-subtitleTin2')}`
                : `${interest}% ${formatText('mortgageRequestPanel-subtitleVariableMortgageText')} + ${differential}% ${formatText('mortgageRequestPanel-subtitleTin2')}`;
        case '03':
            return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
                ? `${interest}% ${formatText('mortgageRequestPanel-mixedMortgagesPanelSubtitleText', [fixedTerm, secondPeriodIntrest])} + ${differential}% ${formatText('mortgageRequestPanel-subtitleTin2')}`
                : `${interest}% ${formatText('mortgageRequestPanel-subtitleTin1', fixedTerm)} + ${differential}% ${formatText('mortgageRequestPanel-subtitleTin2')}`;

        default:
            return '';
    }
}

const getMortgagesConditionsSubtitleText = (cuotaMensual, cuotaMensualResto, isVariableMortgages) => {
    const firstPeriodIntrest = MortgageRequestCurrencyHelp(getMortgageFeeDecimalValue(cuotaMensual));
    const secondPeriodIntrest = MortgageRequestCurrencyHelp(getMortgageFeeDecimalValue(cuotaMensualResto));

    return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
        ? (<div className="mortgage-request-info__wrapper">
            <div className="mortgage-request-info__wrapper-row mortgage-request-info__amount">
                {firstPeriodIntrest}
                <span className="mortgage-request-info__message mortgage-request-info__align-left">
                    {isVariableMortgages
                        ? formatText('mortgageRequestPanel-variableMortgagesPeriodIntrest')
                        : formatText('mortgageRequestPanel-mixedMortgagesPeriodIntrest', ['10'])}
                </span>
            </div>
            <div className="mortgage-request-info__wrapper-row mortgage-request-info__amount">
                {secondPeriodIntrest}
                <span className="mortgage-request-info__message mortgage-request-info__align-left">
                    {formatText('mortgageRequestPanel-subtitleTin2')}
                </span>
            </div>
        </div>)
        : firstPeriodIntrest;
}

const mortgagesContionsMonthlyFeeSubtitleText = (conditions) => {
    const interestType = conditions.get('tipoInteres', '') || '';
    const cuotaMensualResto = conditions.get('cuotaMensualResto');
    const cuotaMensual = conditions.get('cuotaMensual');

    switch (interestType) {
        case '01':
            return MortgageRequestCurrencyHelp({ currency: cuotaMensual });
        case '02':
            return getMortgagesConditionsSubtitleText(cuotaMensual, cuotaMensualResto, true);
        case '03':
            return getMortgagesConditionsSubtitleText(cuotaMensual, cuotaMensualResto, false);

        default:
            return '';
    }
}

const getIntrestParams = (monthlyFee) => {
    const PERIODICY = formatText('mortgageRequestMortgageEdit-periodicy');
    return {
        currency: monthlyFee,
        symbolCurrency: GENERAL_DATA.CURRENCY,
        periodicy: PERIODICY,
        isFormated: true,
        classes: {
            int: 'mortgage-request-mortgage-edit-form__quote-data__amount--int',
            decimal: 'mortgage-request-mortgage-edit-form__quote-data__amount--decimal',
            symbol: 'mortgage-request-mortgage-edit-form__quote-data__amount--periodicity'
        },
        withDecimals: true
    };
}
const getModifyMortgageSubtitleText = (cuotaMensual, cuotaMensualResto, isVariableMortgages, newFirstPeriodIntrest, newSecondPeriodIntrest, plazoFijo) => {
    return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
        ? (<div className="mortgage-request-info__wrapper">
            <div className="mortgage-request-info__wrapper-row mortgage-request-info__amount">
                {newFirstPeriodIntrest}
                <span className="mortgage-request-info__message mortgage-request-info__align-left">
                    {isVariableMortgages
                        ? formatText('mortgageRequestPanel-variableMortgagesPeriodIntrest')
                        : formatText('mortgageRequestPanel-mixedMortgagesPeriodIntrest', [plazoFijo])}
                </span>
            </div>
            <div className="mortgage-request-info__wrapper-row mortgage-request-info__amount">
                {newSecondPeriodIntrest}
                <span className="mortgage-request-info__message mortgage-request-info__align-left">
                    {formatText('mortgageRequestPanel-subtitleTin2')}
                </span>
            </div>
        </div>)
        : newFirstPeriodIntrest;
}


const modifyMortgageMonthlyFeeSubtitleText = (interestType, cuotaMensual, cuotaMensualResto, valFixedTerm, plazoFijo) => {
    const newFirstPeriodIntrest = MortgageRequestCurrencyHelp(getIntrestParams(cuotaMensual));
    const newSecondPeriodIntrest = MortgageRequestCurrencyHelp(getIntrestParams(cuotaMensualResto));
    const interestTypeByMortgage = MORTGAGE_TYPE_BY_PRODUCT_TYPE[interestType];
    switch (interestTypeByMortgage) {
        case 'FIJO':
            return newFirstPeriodIntrest;
        case 'VARIABLE':
            return getModifyMortgageSubtitleText(cuotaMensual, cuotaMensualResto, true, newFirstPeriodIntrest, newSecondPeriodIntrest);
        case 'MIXTO':
            return getModifyMortgageSubtitleText(cuotaMensual, cuotaMensualResto, false, newFirstPeriodIntrest, newSecondPeriodIntrest, valFixedTerm, plazoFijo);
        default:
            return '';
    }
}

const modifyMortgageMonthlyFeeTitleText = (interestType, cuotaMensual, cuotaMensualResto) => {
    const mortgageMonthlyFeeTitle = formatText('mortgageRequestMortgageEdit-quoteTitle');
    const interestTypeByMortgage = MORTGAGE_TYPE_BY_PRODUCT_TYPE[interestType];
    switch (interestTypeByMortgage) {
        case 'FIJO':
            return mortgageMonthlyFeeTitle;
        case 'VARIABLE':
            return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
                ? <FormattedText value="mortgageRequestMortgageEdit-quoteTitle1" />
                : mortgageMonthlyFeeTitle;
        case 'MIXTO':
            return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
                ? <FormattedText value="mortgageRequestMortgageEdit-quoteTitle1" />
                : mortgageMonthlyFeeTitle;

        default:
            return '';
    }
}

const getModifyMortgageLegalText = (cuotaMensual, cuotaMensualResto) => {
    return validateSecondPeriodIntrest(cuotaMensual, cuotaMensualResto)
        ? <div className={`mortgage-request-mortgage-edit-view__wrapper-footer
            ${__CONTACT_CENTER__
                ? 'mortgage-request-mortgage-edit-view__legal-text-cc'
                : 'mortgage-request-mortgage-edit-view__legal-text'}`
        }>
            <FormattedText value="mortgageRequestMortgageEdit-mortgageModifyLegalText" />
        </div>
        : '';
}

const modifyMortgageLegalText = (interestType, cuotaMensual, cuotaMensualResto) => {
    const interestTypeByMortgage = MORTGAGE_TYPE_BY_PRODUCT_TYPE[interestType]
    switch (interestTypeByMortgage) {
        case 'VARIABLE':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)
        case 'MIXTO':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)

        default:
            return '';
    }
}

const getMortgageCounterOfferText = (formattedFee, classes) => {
    return (<p className={classes.cardFee}>{`${formattedFee} / `}<span className={classes.cardQuotaText}>
        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_monthFee" />
    </span></p>)
}

const mortgageCounterOfferText = (interestType, fee, feeRest, classes) => {
    const formattedFee = MortgageRequestCurrencyHelp({ currency: fee, symbol: EURO_TEXT });
    const formattedFeeRest = MortgageRequestCurrencyHelp({ currency: feeRest, symbol: EURO_TEXT });

    switch (interestType) {
        case 'Fija':
            return getMortgageCounterOfferText(formattedFee, classes)

        case 'Variable':
            return validateSecondPeriodIntrest(fee, feeRest)
                ? (<p className={classes.cardFee}>
                    {`${formattedFee} / `}
                    <span className={classes.cardQuotaText}>
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_monthFee" />
                        {`${formatText('mortgageRequestPanel-variableMortgagesPeriodIntrest')}`}
                    </span>
                    <br />
                    {`${formattedFeeRest} / `}
                    <span className={classes.cardQuotaText}>
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_monthFee" />
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_elRestoText" />
                    </span>
                </p>)
                : getMortgageCounterOfferText(formattedFee, classes)

        case 'Mixta':
            return validateSecondPeriodIntrest(fee, feeRest)
                ? (<p className={classes.cardFee}>
                    {`${formattedFee} / `}
                    <span className={classes.cardQuotaText}>
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_monthFee" />
                        {`${formatText('mortgageRequestPanel-mixedMortgagesPeriodIntrest', ['10'])}`}
                    </span>
                    <br />
                    {`${formattedFeeRest} / `}
                    <span className={classes.cardQuotaText}>
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_monthFee" />
                        <FormattedText value="mortgageRequestPreApprovalCounterOffer-mortgageRequestPreApprovalCounterOffer_elRestoText" />
                    </span>
                </p>)
                : getMortgageCounterOfferText(formattedFee, classes)

        default:
            return '';
    }
}

const mortgageCounterOfferLegalText = (interestType, cuotaMensual, cuotaMensualResto) => {
    switch (interestType) {
        case 'Variable':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)
        case 'Mixta':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)

        default:
            return '';
    }
}

const mortgagePreApprovalLegalText = (conditions) => {
    const interestType = conditions.get('tipoInteres', '') || '';
    const cuotaMensualResto = conditions.get('cuotaMensualResto');
    const cuotaMensual = conditions.get('cuotaMensual');

    switch (interestType) {
        case '02':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)
        case '03':
            return getModifyMortgageLegalText(cuotaMensual, cuotaMensualResto)

        default:
            return '';
    }
}

module.exports = {
    shouldGoToEstatesListPage,
    getEstatesToRequest,
    checkIfBudgetIsConfirmed,
    mortgagesPreApprovalIntrestPeriodSubtitle,
    mortgagesMonthlyFeeSubtitleText,
    mortgagesPanelSubtitleText,
    mortgagesContionsMonthlyFeeSubtitleText,
    modifyMortgageMonthlyFeeSubtitleText,
    modifyMortgageMonthlyFeeTitleText,
    modifyMortgageLegalText,
    mortgageCounterOfferText,
    mortgageCounterOfferLegalText,
    mortgagePreApprovalLegalText,
}
